// ─────────────────────────────────────────────────────────────
// SITEBUILDER.SOLUTIONS — EDITABLE CONTENT
// Everything on the page reads from this file. Change the text
// here and the site updates. No layout code to touch.
// ─────────────────────────────────────────────────────────────

export const brand = {
  nameStrong: 'SITEBUILDER',
  nameAccent: '.SOLUTIONS',
  phone: '(555) 019-4432',
  email: 'hello@sitebuilder.solutions',
  tagline: 'SEO websites built to rank and convert.',
}

export const nav = [
  { label: 'Tools', href: '#tools' },
  { label: 'Build Notes', href: '#process' },
  { label: 'Results', href: '#results' },
]

export const hero = {
  headlineLine1: 'You want a website. Good.',
  headlineLine2: 'But is that all you want?',
  subhead:
    "Imagine a car with no engine or transmission – looks great, but going nowhere fast. That's most websites.",
  missing: [
    { strong: 'Not', rest: 'built for SEO (search engine optimization)' },
    { strong: 'No', rest: 'customer feedback loop' },
    { strong: 'No', rest: 'content engine (the fuel for SEO)' },
    { strong: 'No', rest: 'push for reviews' },
    { strong: 'No', rest: 'technical foundation – schema, OG tags, H1s, and more' },
  ],
  yesLine: 'We build websites differently.',
  yesSub: 'Style & Function',
  ctaLabel: 'Find My Business',
  ctaNote: 'to get started.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/e23164db-51b1-46c0-959d-08f766c2ec5b.jpg',
}

// THE COMPLETE BUILD — the car-parts diagram.
// hotspot x/y are percentages of the image (0–100), used to place the
// clickable markers over each part. Swap `image` for Vora's Canva export
// and nudge the x/y values to line the markers up again.
export const engine = {
  eyebrow: 'The complete build',
  headingLead: 'A website needs an',
  headingAccent: 'engine.',
  intro:
    'A great-looking site with nothing under the hood goes nowhere. Think about it like a car: every part has a job. Here is everything we engineer into yours.',
  hint: 'Tap any part to see what it does for your site.',
  closer: { lead: 'Style', accent: 'and', rest: 'function. Every part, engineered to drive.' },
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/8b9d348d-319d-491e-bf56-bf6241f8b864.jpg',
  parts: [
    {
      part: 'Engine',
      title: 'The Content Engine',
      body: 'An engine needs fuel. Content is the fuel – fresh SEO articles every month plus authority-building backlinks that pull in real search traffic.',
      hotspot: { x: 12.5, y: 66 },
    },
    {
      part: 'Transmission',
      title: 'Conversion & Persuasion',
      body: 'Transmission turns raw power into forward motion. We turn visitors into customers, with sales psychology and persuasion built into every page.',
      hotspot: { x: 29, y: 69 },
    },
    {
      part: 'Suspension',
      title: 'Guard Rails & Site Rules',
      body: 'Suspension keeps you on the road through every bump. Our build standards keep your site fast, stable, and on the right track.',
      hotspot: { x: 41, y: 65 },
    },
    {
      part: 'Brake System',
      title: 'Reviews & Feedback Loop',
      body: 'Brakes keep you in control. A live review loop captures customer feedback and builds the trust that closes the sale.',
      hotspot: { x: 50, y: 68 },
    },
    {
      part: 'Steering System',
      title: 'Navigation & UX',
      body: 'Steering points you exactly where you need to go. Clear navigation guides every visitor straight to the action you want.',
      hotspot: { x: 62, y: 69 },
    },
    {
      part: 'ECU',
      title: 'Technical Foundation',
      body: 'The ECU is the brain the machines read. Schema, OG tags, and image compression are the technical signals search engines trust.',
      hotspot: { x: 75, y: 72 },
    },
    {
      part: 'Battery',
      title: 'Hosting & Power',
      body: 'No battery, no spark. Rock-solid hosting and infrastructure keep your site online, fast, and always on.',
      hotspot: { x: 87, y: 72 },
    },
  ],
}

export const results = {
  eyebrow: 'Results',
  heading: 'Built to rank. Measured to prove it.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/f4cc5eee-6e62-4d75-844e-ca53c907b8fb.jpg',
  stats: [
    { value: '3.4x', label: 'Average lift in organic calls within 6 months' },
    { value: '#1–3', label: 'Map pack placement for target service keywords' },
    { value: '96', label: 'Median PageSpeed score across live builds' },
    { value: '210+', label: 'Local businesses launched and maintained' },
  ],
}

export const process = {
  eyebrow: 'Build notes',
  heading: 'How a build goes',
  steps: [
    {
      title: 'Find your business',
      body: 'We pull your existing listings, reviews and rankings and show you exactly where you stand against the shops beating you.',
    },
    {
      title: 'Build the body and the engine',
      body: 'Design, copy, and the full technical stack ship together. Typically live in two to three weeks.',
    },
    {
      title: 'Run it every month',
      body: 'New content, review pushes, and reporting keep compounding after launch. You update anything you want, anytime.',
    },
  ],
}

export const tools = {
  eyebrow: 'Tools',
  heading: 'Free tools before you spend a dollar',
  items: [
    {
      name: 'Business Finder',
      body: 'See your live Google listing, review count and ranking snapshot in seconds.',
      cta: 'Run a scan',
    },
    {
      name: 'Site Audit',
      body: 'Speed, schema, tags and mobile health graded page by page.',
      cta: 'Audit my site',
    },
    {
      name: 'Keyword Gap',
      body: 'The search terms your competitors rank for and you do not.',
      cta: 'Find the gaps',
    },
  ],
}

export const pricing = {
  eyebrow: 'Pricing',
  heading: 'One build. One monthly engine.',
  note: 'No contracts. Cancel the engine anytime — the site stays yours.',
  plans: [
    {
      name: 'The Build',
      price: '$2,950',
      cadence: 'one time',
      body: 'Custom design, copywriting, and the full technical SEO foundation.',
      features: ['Up to 8 pages', 'Schema + OG + sitemap', 'Mobile and speed tuned', 'Live in 2–3 weeks'],
      cta: 'Buy Now',
      featured: true,
    },
    {
      name: 'The Engine',
      price: '$495',
      cadence: 'per month',
      body: 'The part that keeps the rankings moving after launch.',
      features: ['4 new content pages / mo', 'Review push automation', 'Feedback loop inbox', 'Monthly reporting call'],
      cta: 'Start the engine',
      featured: false,
    },
  ],
}

export const reviews = {
  eyebrow: 'In the shop',
  heading: 'What owners say',
  items: [
    {
      quote:
        'We went from page four to the map pack in about five months. The phone actually rings on Mondays now.',
      name: 'Marcus Webb',
      role: 'Owner, Webb Auto Detail',
    },
    {
      quote:
        'Third site we have paid for. First one that came with anything behind the pretty pictures.',
      name: 'Dana Ruiz',
      role: 'Ruiz Plumbing & Drain',
    },
    {
      quote:
        'The review automation alone paid for the whole build. 41 new Google reviews in a quarter.',
      name: 'Tyler Nguyen',
      role: 'Apex Roofing Co.',
    },
  ],
}

export const finalCta = {
  heading: 'Pop the hood on your current site.',
  body: 'Free scan, no call required. See your rankings, reviews and technical gaps in under a minute.',
  primary: 'Find My Business',
  secondary: 'Buy Now',
}

export const footer = {
  columns: [
    { title: 'Product', links: ['Tools', 'Build Notes', 'Results', 'Pricing'] },
    { title: 'Company', links: ['About', 'Contact', 'Careers'] },
    { title: 'Legal', links: ['Privacy', 'Terms', 'Accessibility'] },
  ],
  copyright: '© 2026 SiteBuilder Solutions. All rights reserved.',
}
